import { TsDemuxer, FMP4Remuxer, WarningType } from 'xgplayer-transmuxer'
import { MSE, Buffer, MSEErrorType, EVENT, ERR, StreamingError, Logger } from 'xgplayer-streaming-shared'

const logger = new Logger('BufferService')

const crypto = window.crypto || window.msCrypto
const subtle = crypto && (crypto.subtle || crypto.webkitSubtle)

export class BufferService {
  _demuxer = new TsDemuxer()
  _remuxer = null
  _mse = null
  _softVideo = null

  _mseOpened = false
  _sourceCreated = false
  _needInitSegment = true

  constructor (hls, softVideo) {
    this.hls = hls

    if (softVideo) { // soft decode
      this._softVideo = softVideo
    } else {
      this._remuxer = new FMP4Remuxer(this._demuxer.videoTrack, this._demuxer.audioTrack)
      this._mse = new MSE()
      this._mse.bindMedia(hls.media)
    }
  }

  async updateDuration (duration) {
    if (this._mse) {
      if (!this._mseOpened) {
        await this._mse.open()
        this._mseOpened = true
      }
      try {
        logger.debug('update duration', duration)
        await this._mse.updateDuration(duration)
      } catch (error) {
        if (error.type === MSEErrorType.CANCELLED) return
        throw new StreamingError(ERR.MSE, error)
      }
      return
    }
    if (this._softVideo) {
      this._softVideo.duration = duration
    }
  }

  async appendBuffer (chunk, discontinuity, contiguous, startTime) {
    const demuxer = this._demuxer
    if (!chunk || !chunk.length || !demuxer) return

    try {
      demuxer.demuxAndFix(chunk, discontinuity, contiguous, startTime)
    } catch (error) {
      throw new StreamingError(ERR.DEMUX, error)
    }
    const { videoTrack, audioTrack, metadataTrack } = demuxer
    const videoType = videoTrack.type
    const audioType = audioTrack.type

    this._fireEvents(videoTrack, audioTrack, metadataTrack, discontinuity)

    const mse = this._mse
    if (mse) {
      if (!this._mseOpened) {
        await mse.open()
        this._mseOpened = true
      }

      if (!this._sourceCreated) {
        try {
          if (videoTrack.exist()) {
            mse.createSource(videoType, `video/mp4;codecs=${videoTrack.codec}`)
            this._sourceCreated = true
          }
          if (audioTrack.exist()) {
            mse.createSource(audioType, `audio/mp4;codecs=${audioTrack.codec}`)
            this._sourceCreated = true
          }
        } catch (error) {
          throw new StreamingError(ERR.MSE, error)
        }
      }

      let remuxResult
      try {
        remuxResult = this._remuxer.remux(this._needInitSegment || discontinuity)
      } catch (error) {
        throw new StreamingError(ERR.REMUX, error)
      }

      this._needInitSegment = false

      let p = []
      if (remuxResult.videoInitSegment) p.push(mse.append(videoType, remuxResult.videoInitSegment))
      if (remuxResult.audioInitSegment) p.push(mse.append(audioType, remuxResult.audioInitSegment))
      if (remuxResult.videoSegment) p.push(mse.append(videoType, remuxResult.videoSegment))
      if (remuxResult.audioSegment) p.push(mse.append(audioType, remuxResult.audioSegment))

      return Promise.all(p).catch(error => {
        if (error.type === MSEErrorType.CANCELLED) return
        throw new StreamingError(ERR.MSE, error)
      })
    } else if (this._softVideo) {
      this._softVideo.appendBuffer(videoTrack, audioTrack)
    }
  }

  async evictBuffer (bufferBehind) {
    const media = this.hls.media
    if (!this._mse || !this._demuxer || !media || !bufferBehind || bufferBehind < 0) return
    const currentTime = media.currentTime
    const removeEnd = currentTime - bufferBehind
    if (removeEnd <= 0) return
    const start = Buffer.start(Buffer.get(media))
    if (start + 1 >= removeEnd) return
    const demuxer = this._demuxer

    const p = [];
    [demuxer.videoTrack, demuxer.audioTrack].forEach((track) => {
      if (track.exist()) {
        p.push(this._mse.remove(track.type, 0, removeEnd))
      }
    })

    return Promise.all(p).catch((error) => {
      if (error.type === MSEErrorType.CANCELLED) return
      throw new StreamingError(ERR.MSE, error)
    })
  }

  async decryptBuffer (data, key, iv) {
    if (!subtle) throw new StreamingError(ERR.DECRYPT, null, null, 'window.crypto is not exist')
    let buffer
    try {
      const aesKey = await subtle.importKey('raw', key, { name: 'AES-CBC' }, false, ['encrypt', 'decrypt'])
      buffer = await subtle.decrypt({ name: 'AES-CBC', iv }, aesKey, data)
    } catch (error) {
      throw new StreamingError(ERR.DECRYPT, error)
    }
    return new Uint8Array(buffer)
  }

  async reset () {
    if (this._mse) {
      await this._mse.unbindMedia()
      await this._mse.bindMedia(this.hls.media)
      this._mseOpened = false
    }
    this._sourceCreated = false
    this._needInitSegment = true
  }

  async endOfStream () {
    if (this._mse) {
      if (this._sourceCreated) {
        try {
          await this._mse.endOfStream()
        } catch (error) {
          if (error.type === MSEErrorType.CANCELLED) return
          throw new StreamingError(ERR.MSE, error)
        }
      }
    }
    if (this._softVideo) {
      this._softVideo.endOfStream()
    }
  }

  async setLiveSeekableRange (start, end) {
    if (!this._mse) return
    this._mse.setLiveSeekableRange(start, end)
  }

  async destroy () {
    const mse = this._mse
    this._mse = null
    this._softVideo = null
    this._demuxer = null
    this._remuxer = null

    if (mse) {
      await mse.unbindMedia()
    }
  }

  _fireEvents (videoTrack, audioTrack, metadataTrack, discontinuity) {
    logger.debug(videoTrack.samples, audioTrack.samples)

    if (discontinuity) {
      if (videoTrack.exist()) {
        this.hls.emit(EVENT.METADATA_PARSED, {
          type: 'video',
          meta: {
            codec: videoTrack.codec,
            timescale: videoTrack.timescale,
            width: videoTrack.width,
            height: videoTrack.height,
            sarRatio: videoTrack.sarRatio,
            baseDts: videoTrack.baseDts
          }
        })
      }

      if (audioTrack.exist()) {
        this.hls.emit(EVENT.METADATA_PARSED, {
          type: 'audio',
          meta: {
            codec: audioTrack.codec,
            channelCount: audioTrack.channelCount,
            sampleRate: audioTrack.sampleRate,
            timescale: audioTrack.timescale,
            baseDts: audioTrack.baseDts
          }
        })
      }

      logger.debug('discontinuity', videoTrack, audioTrack)
    }

    videoTrack.warnings.forEach(warn => {
      let type
      switch (warn.type) {
        case WarningType.LARGE_AV_SHIFT:
          type = EVENT.LARGE_AV_FIRST_FRAME_GAP_DETECT
          break
        case WarningType.LARGE_VIDEO_GAP:
          type = EVENT.LARGE_VIDEO_DTS_GAP_DETECT
          break
        case WarningType.LARGE_VIDEO_GAP_BETWEEN_CHUNK:
          type = EVENT.MAX_DTS_DELTA_WITH_NEXT_SEGMENT_DETECT
          break
      }

      if (type) this.hls.emit(EVENT.STREAM_EXCEPTION, { ...warn, type })
      logger.warn('video exception', warn)
    })

    audioTrack.warnings.forEach(warn => {
      let type
      switch (warn.type) {
        case WarningType.LARGE_AUDIO_GAP:
          type = EVENT.LARGE_AUDIO_DTS_GAP_DETECT
          break
        case WarningType.AUDIO_FILLED:
          type = EVENT.AUDIO_GAP_DETECT
          break
        case WarningType.AUDIO_DROPPED:
          type = EVENT.AUDIO_OVERLAP_DETECT
          break
      }

      if (type) this.hls.emit(EVENT.STREAM_EXCEPTION, { ...warn, type })
      logger.warn('audio exception', warn)
    })

    videoTrack.samples.forEach((sample) => {
      if (sample.keyframe) {
        this.hls.emit(EVENT.KEYFRAME, { pts: sample.pts })
      }
    })

    metadataTrack.seiSamples.forEach(sei => {
      this.hls.emit(EVENT.SEI, {
        ...sei,
        sei: {
          code: sei.data.type,
          content: sei.data.payload,
          dts: sei.pts
        }
      })
    })
  }
}
